import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

export default function CasoHemilaminectomiaNoa() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-teal-600 transition-colors">Inicio</Link>
            <span>/</span>
            <Link to="/casos-clinicos" className="hover:text-teal-600 transition-colors">Casos Clínicos</Link>
            <span>/</span>
            <span className="text-teal-600">Hemilaminectomía Cervical - Noa</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                Neurocirugía Veterinaria
              </div>
              <h1 className="text-4xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6 leading-tight">
                Hemilaminectomía Cervical Lateral por Hernia Discal Foraminal
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                El caso de Noa: Recuperación exitosa en una perra geriátrica mediante cirugía especializada en Almería
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center space-x-2">
                  <i className="ri-hospital-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">Clínica Veterinaria Vega de Acá</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">Almería</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-calendar-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">Enero 2025</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/47f87a051710ed22e8f004c2fd7b5d66.jpg"
                  alt="Noa recuperada tras hemilaminectomía cervical"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-l-4 border-teal-600">
                <p className="text-sm text-gray-600 mb-1">Paciente</p>
                <p className="text-2xl font-playfair font-bold text-gray-900">Noa, 13 años</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resumen para Propietarios */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
              <i className="ri-heart-pulse-line text-teal-600 text-2xl"></i>
            </div>
            <h2 className="text-3xl font-playfair font-bold text-gray-900">
              🐾 Resumen para Propietarios: El caso de Noa
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border border-teal-100">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Noa, una perra de 13 años, llegó a nuestra clínica con un dolor cervical tan intenso que ya no respondía a la medicación más fuerte. Gracias a la rápida detección de su veterinario de confianza, el <strong className="text-teal-700">Dr. Ignacio Cadenas</strong>, el caso fue derivado a nuestra consulta de especialidad.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              La intervención y las pruebas se realizaron en las instalaciones de la <strong className="text-teal-700">Clínica Veterinaria Vega de Acá (Almería)</strong>, donde pudimos aplicar técnicas de cirugía avanzada para devolverle a Noa una vida sin dolor.
            </p>
            <div className="bg-white p-6 rounded-xl border-l-4 border-teal-600">
              <p className="text-lg text-gray-900 font-semibold">
                ✨ Hoy, Noa vuelve a caminar feliz, demostrando que la edad no es un impedimento para una cirugía exitosa cuando el diagnóstico y la técnica son los adecuados.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnóstico por Imagen */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-12">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
              <i className="ri-image-line text-teal-600 text-2xl"></i>
            </div>
            <h2 className="text-3xl font-playfair font-bold text-gray-900">
              🖼️ Diagnóstico por Imagen Avanzada
            </h2>
          </div>

          <p className="text-lg text-gray-700 mb-12">
            Para planificar la cirugía con éxito, fue fundamental el estudio mediante <strong className="text-teal-700">Resonancia Magnética</strong>:
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Imagen 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-[4/3] w-full h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=Veterinary%20cervical%20spine%20MRI%20sagittal%20view%20showing%20vertebral%20levels%20C2%20C6%20C7%20labeled%2C%20professional%20diagnostic%20imaging%20for%20foraminal%20disc%20herniation%20in%20dog%2C%20high%20quality%20medical%20imaging%20with%20clear%20anatomical%20landmarks&width=600&height=450&seq=caso-noa-mri-sagital-01&orientation=landscape"
                  alt="Resonancia Magnética Sagital - Niveles vertebrales C2, C6, C7"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Imagen 1: Estudio Sagital</h3>
                <p className="text-gray-600">
                  Se identifican los niveles vertebrales clave (C2, C6, C7) para la planificación quirúrgica.
                </p>
              </div>
            </div>

            {/* Imagen 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-[4/3] w-full h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=Veterinary%20cervical%20MRI%20showing%20foraminal%20disc%20herniation%20C6-C7%20with%20spinal%20cord%20compression%2C%20multiple%20disc%20degeneration%20marked%20with%20arrows%2C%20axial%20and%20sagittal%20views%20combined%20diagnostic%20imaging%20for%20canine%20cervical%20disc%20disease&width=600&height=450&seq=caso-noa-mri-hernia-01&orientation=landscape"
                  alt="Resonancia Magnética - Hernia discal C2-C3 y C6-C7"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Imagen 2: Identificación de Hernias</h3>
                <p className="text-gray-600 mb-3">
                  <span className="text-red-600 font-semibold">Flecha roja:</span> Hernia C2-C3
                </p>
                <p className="text-gray-600 mb-3">
                  <span className="text-yellow-600 font-semibold">Puntas amarillas:</span> Degeneración discal múltiple
                </p>
                <p className="text-gray-600">
                  <span className="text-orange-600 font-semibold">Flechas naranjas:</span> Extrusión C6-C7 comprimiendo lateralmente la médula y el foramen
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informe para Profesionales */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-12">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
              <i className="ri-stethoscope-line text-teal-600 text-2xl"></i>
            </div>
            <h2 className="text-3xl font-playfair font-bold text-gray-900">
              🩺 Informe para Profesionales
            </h2>
          </div>

          {/* Colaboración Clínica */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-team-line text-teal-600 mr-3 text-2xl"></i>
              Colaboración Clínica
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Veterinario Referente</p>
                <p className="text-lg font-semibold text-gray-900">Dr. Ignacio Cadenas</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Centro Quirúrgico</p>
                <p className="text-lg font-semibold text-gray-900">Clínica Veterinaria Vega de Acá</p>
                <p className="text-sm text-gray-600">Almería</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Cirujano Especialista</p>
                <p className="text-lg font-semibold text-gray-900">Pablo Conejero</p>
              </div>
            </div>
          </div>

          {/* Resumen del Caso */}
          <div className="mb-8">
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-4">Resumen del Caso</h3>
            <p className="text-gray-700 leading-relaxed">
              Perra geriátrica remitida por cojera progresiva y dolor intenso en el miembro torácico izquierdo, refractarios a tratamiento médico multimodal. La resonancia magnética identificó una <strong className="text-teal-700">hernia discal cervical foraminal izquierda C6–C7</strong>, responsable de la clínica radicular, junto con una hernia discal C2–C3 concurrente no sintomática. Dada la localización lateralizada de la lesión (con compresión medular muy lateralizada a la izquierda), la persistencia del dolor y la ausencia de respuesta al manejo conservador, se indicó derivación para descompresión mediante hemilaminectomía cervical lateral.
            </p>
          </div>

          {/* Diagnóstico */}
          <div className="mb-8">
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-4">Diagnóstico</h3>
            <div className="bg-teal-50 p-6 rounded-xl border-l-4 border-teal-600">
              <p className="text-gray-900 mb-3">
                <strong className="text-teal-700">Diagnóstico principal:</strong> Hernia discal cervical foraminal izquierda C6–C7 (extrusión) con compresión radicular y medular lateralizada.
              </p>
              <p className="text-gray-900">
                <strong className="text-teal-700">Diagnóstico concurrente:</strong> Hernia discal cervical C2–C3 (extrusión) sin expresión clínica actual.
              </p>
            </div>
          </div>

          {/* Plan Quirúrgico */}
          <div className="mb-8">
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-4">Plan Quirúrgico y Justificación</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Se indicó descompresión quirúrgica mediante <strong className="text-teal-700">hemilaminectomía cervical lateral izquierda C6–C7</strong>, utilizando un abordaje lateral modificado. Este abordaje permite una visualización directa del aspecto lateral y ventrolateral del canal vertebral y de la raíz nerviosa afectada, siendo más adecuado que el ventral slot cuando la compresión no es central.
            </p>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="font-semibold text-gray-900 mb-3">Las principales razones para la elección de este abordaje fueron:</p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">Dolor radicular persistente y refractario</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">Lesión claramente lateralizada en el foramen intervertebral con compromiso del parénquima medular</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">Necesidad de una descompresión eficaz minimizando la manipulación medular</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Técnica Quirúrgica */}
          <div className="mb-8">
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-4">Técnica Quirúrgica</h3>
            <p className="text-gray-700 leading-relaxed">
              Se realizó una <strong className="text-teal-700">hemilaminectomía cervical lateral izquierda</strong>. Con la paciente en decúbito lateral, se procedió al fresado cuidadoso de la lámina vertebral hasta acceder al canal medular, identificando el material discal extrusionado en el foramen intervertebral. Se extrajo el material compresivo logrando la descompresión de la médula y de la raíz nerviosa afectada. El abordaje permitió una exposición selectiva de la faceta articular y del foramen con mínima alteración de la musculatura epaxial.
            </p>
          </div>

          {/* Evolución */}
          <div className="mb-8">
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-4">Evolución y Resultado Final</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              La evolución neurológica fue progresivamente favorable, con mejoría del dolor cervical y recuperación funcional acorde a los tiempos esperables. La conservación de la propiocepción permitió anticipar una recuperación gradual, con retorno a la deambulación y resolución del cuadro radicular.
            </p>
            
            {/* Video Placeholder */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-video w-full flex items-center justify-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-600/20 to-transparent"></div>
                <div className="text-center z-10">
                  <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white/30">
                    <i className="ri-play-fill text-white text-4xl"></i>
                  </div>
                  <p className="text-white text-lg font-semibold mb-2">Vídeo: Evolución de Noa</p>
                  <p className="text-white/80 text-sm">Noa caminando en revisión tras la cirugía</p>
                </div>
              </div>
              <div className="p-6 bg-gray-900">
                <p className="text-gray-300 text-sm">
                  <i className="ri-information-line text-teal-400 mr-2"></i>
                  Evolución funcional de Noa tras la cirugía realizada en Clínica Veterinaria Vega de Acá, Almería.
                </p>
              </div>
            </div>
          </div>

          {/* Puntos Clave */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-8 rounded-2xl text-white">
            <h3 className="text-2xl font-playfair font-bold mb-6 flex items-center">
              <i className="ri-lightbulb-line mr-3 text-3xl"></i>
              💡 Puntos Clave para el Veterinario Referente
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-arrow-right-line"></i>
                </div>
                <span className="text-white/95">Derivar ante dolor cervical intenso o cojera de miembro anterior refractaria a tratamiento médico</span>
              </li>
              <li className="flex items-start">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-arrow-right-line"></i>
                </div>
                <span className="text-white/95">Las hernias discales foraminales/lateralizadas son una indicación clara de cirugía especializada</span>
              </li>
              <li className="flex items-start">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                  <i className="ri-arrow-right-line"></i>
                </div>
                <span className="text-white/95">El abordaje lateral modificado permite minimizar daño muscular y morbilidad postoperatoria, siendo ideal para pacientes geriátricos</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-6">
            ¿Tienes un Caso Similar?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Si tienes un paciente con dolor cervical refractario o sospecha de hernia discal foraminal, podemos ayudarte con el diagnóstico y tratamiento especializado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+34644428733"
              className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2 text-xl"></i>
              Llamar para Referir un Caso
            </a>
            <Link 
              to="/casos-clinicos"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-full hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl border-2 border-teal-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-folder-line mr-2 text-xl"></i>
              Ver Más Casos Clínicos
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
